import {
  add,
  dist,
  fromPolar,
  normalize,
  normalizeDegrees,
  perp,
  scale,
  segment,
  sub,
  vec,
  type Shape,
  type Vec2,
} from '../geom';
import { DEFAULT_DISPLAY, type LengthDisplay, type Style } from '../model';
import type { DimensionLook, DimensionText } from './types';

/** What a dimension looks like when neither it nor its layer sets a size. */
export const DEFAULT_LOOK: DimensionLook = {
  textHeight: 3.5,
  arrowSize: 3,
  arrow: 'closed',
  display: DEFAULT_DISPLAY,
};

/** Characters are about this wide for their height, in a plain sans-serif font. */
const CHARACTER_WIDTH = 0.6;

export const lookOf = (style: Style | undefined, display: LengthDisplay): DimensionLook => ({
  textHeight: style?.textHeight ?? DEFAULT_LOOK.textHeight,
  arrowSize: style?.arrowSize ?? DEFAULT_LOOK.arrowSize,
  arrow: style?.arrow ?? DEFAULT_LOOK.arrow,
  display,
});

/** The space between text and its line, and between an extension line and what it measures. */
export const gapOf = (look: DimensionLook): number => look.textHeight * 0.3;

/** A direction turned so text along it never reads upside down, in (-90, 90]. */
export function readable(degrees: number): number {
  const d = normalizeDegrees(degrees);
  return d > 90 && d <= 270 ? d - 180 : d > 270 ? d - 360 : d;
}

/** Text centered `along` a line at `mid`, on the side above it as it reads. */
export function textAlong(
  mid: Vec2,
  degrees: number,
  content: string,
  look: DimensionLook,
): DimensionText {
  const angle = readable(degrees);
  const up = fromPolar(vec(0, 0), gapOf(look) + look.textHeight / 2, angle + 90);
  return textAt(add(mid, up), angle, content, look);
}

/** How wide `content` sets in `look`'s text height. */
export const textWidth = (content: string, look: DimensionLook): number =>
  content.length * look.textHeight * CHARACTER_WIDTH;

/**
 * Text riding a leader: the leader runs from `start` along `direction`, `from` long before
 * the text, then on under it. Returns the leader's far end and the text.
 */
export function leaderText(
  start: Vec2,
  direction: Vec2,
  degrees: number,
  from: number,
  content: string,
  look: DimensionLook,
): { end: Vec2; text: DimensionText } {
  const width = textWidth(content, look);
  return {
    end: add(start, scale(direction, from + width + gapOf(look))),
    text: textAlong(add(start, scale(direction, from + width / 2)), degrees, content, look),
  };
}

export const textAt = (
  at: Vec2,
  angle: number,
  content: string,
  look: DimensionLook,
): DimensionText => ({
  at,
  angle,
  height: look.textHeight,
  width: textWidth(content, look),
  content,
});

/**
 * The line from what is measured, at `from`, out to the dimension line at `to`: it starts a
 * gap away and runs a little past.
 */
export function extension(from: Vec2, to: Vec2, look: DimensionLook): Shape[] {
  const gap = gapOf(look);
  if (dist(from, to) <= gap) return [];
  const direction = normalize(sub(to, from));
  return [segment(add(from, scale(direction, gap)), add(to, scale(direction, look.arrowSize / 2)))];
}

/** An arrowhead at `tip`, pointing along `direction`, as strokes and fills. */
export function arrowhead(
  tip: Vec2,
  direction: Vec2,
  look: DimensionLook,
): { strokes: Shape[]; fills: Vec2[][] } {
  const size = look.arrowSize;
  const along = normalize(direction);
  const side = perp(along);
  const back = sub(tip, scale(along, size));
  switch (look.arrow) {
    case 'closed':
      return {
        strokes: [],
        fills: [[tip, add(back, scale(side, size / 6)), sub(back, scale(side, size / 6))]],
      };
    case 'open':
      return {
        strokes: [
          segment(tip, add(back, scale(side, size / 4))),
          segment(tip, sub(back, scale(side, size / 4))),
        ],
        fills: [],
      };
    case 'tick': {
      const slant = scale(normalize(add(along, side)), size / 2);
      return { strokes: [segment(sub(tip, slant), add(tip, slant))], fills: [] };
    }
  }
}

/** Strokes and fills from several parts, in order. */
export function joined(parts: readonly { strokes: Shape[]; fills: Vec2[][] }[]): {
  strokes: Shape[];
  fills: Vec2[][];
} {
  return {
    strokes: parts.flatMap((part) => part.strokes),
    fills: parts.flatMap((part) => part.fills),
  };
}
